import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Box, CircularProgress } from "@mui/material";
import { Alert, Typography } from "@platform/design-system";

import {
  clearAuth0Connection,
  handleAuth0Callback,
} from "../../auth/auth0Pkce";

function Auth0Callback(): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef<boolean>(false);

  useEffect(() => {
    // Prevent multiple calls
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    async function processCallback(): Promise<void> {
      try {
        const result = await handleAuth0Callback(location.search);

        if (result.success) {
          // Force a full page reload to reinitialize AuthContext with new tokens
          // This ensures the app recognizes the authentication state
          const redirectTo = result.appState?.returnTo || "";
          window.location.href = `/${redirectTo}`;
        } else if (/invalid state/i.test(result.error || "")) {
          // State mismatch — PKCE transaction was lost (e.g. session cleared mid-flow).
          // Do NOT auto-retry here: it creates a redirect loop when the verifier is gone.
          // Clear the stale connection so the app returns to the Login page instead.
          clearAuth0Connection();
          setError(
            "Authentication session expired. Please try logging in again.",
          );
        } else {
          setError(result.error || "Authentication failed");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error in processCallback:", err);
        setError("Failed to complete authentication. Please try again.");
      }
    }

    processCallback();
  }, [navigate, location]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={2}
    >
      {error ? (
        <>
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            {error}
          </Alert>
          <Typography variant="body-medium">
            Please try logging in again.
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress size={60} />
          <Typography variant="header-small">
            Completing authentication...
          </Typography>
        </>
      )}
    </Box>
  );
}

export default Auth0Callback;
